import {
  CanDeactivateFn,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  Routes,
} from '@angular/router';
import { filter, take } from 'rxjs';
import { inject } from '@angular/core';
import { InventoryService } from './services/inventory.service';
import {
  DefaultInventoryRepository,
  SOFTLINE_MDE_INVENTORY_REPOSITORY,
} from './repositories/inventory.repository';
import { ApiDataAccess } from '@softline/core';
import { InventoryItemService } from './services/inventory-item.service';
import {
  DefaultInventoryItemRepository,
  SOFTLINE_MDE_INVENTORY_ITEM_REPOSITORY,
} from './repositories/inventory-item.repository';
import {
  DefaultInventoryProductDetailRepository,
  SOFTLINE_MDE_INVENTORY_PRODUCT_DETAIL_REPOSITORY,
} from './repositories/inventory-product-detail.repository';
import { inventoryResolveFn } from './resolvers/inventory.resolver';
import { InventoryConfigService } from './services/inventory-config.service';
import { AddInventoryService } from './services/add-inventory.service';
import { RemoteConfigResolver } from '@softline/application';
import { AuthenticationGuard, AuthorizationGuard } from '@softline/auth';

/** Resets after the leave ended: a reset mid-navigation makes the page cancel the leave. */
export const resetInventoryOnLeave: CanDeactivateFn<unknown> = (
  _component,
  _currentRoute,
  _currentState,
  nextState
) => {
  if (nextState.url.includes('/mde-inventur')) return true;
  const inventory = inject(InventoryService);
  inject(Router)
    .events.pipe(
      filter(
        (event) =>
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
      ),
      take(1)
    )
    .subscribe((event) => {
      if (event instanceof NavigationEnd) inventory.reset();
    });
  return true;
};

export const mdeInventoryRoutes: Routes = [
  {
    path: 'mde-inventur',
    providers: [
      InventoryConfigService,
      AddInventoryService,
      {
        provide: SOFTLINE_MDE_INVENTORY_REPOSITORY,
        useFactory: () => new DefaultInventoryRepository(inject(ApiDataAccess)),
      },
      {
        provide: SOFTLINE_MDE_INVENTORY_ITEM_REPOSITORY,
        useFactory: () => new DefaultInventoryItemRepository(inject(ApiDataAccess)),
      },
      {
        provide: SOFTLINE_MDE_INVENTORY_PRODUCT_DETAIL_REPOSITORY,
        useFactory: () =>
          new DefaultInventoryProductDetailRepository(inject(ApiDataAccess)),
      },
      {
        provide: InventoryService,
        useFactory: () => new InventoryService(),
      },
      {
        provide: InventoryItemService,
        useFactory: () => new InventoryItemService(),
      },
    ],
    canActivate: [AuthorizationGuard, AuthenticationGuard],
    canDeactivate: [resetInventoryOnLeave],
    resolve: { config: RemoteConfigResolver },
    children: [
      {
        path: 'start',
        loadComponent: () => import('./pages/start/start.component'),
      },
      {
        path: 'add',
        resolve: {
          activeInventory: inventoryResolveFn,
        },
        loadComponent: () => import('./pages/add/add.component'),
        children: [
          {
            path: 'search',
            loadComponent: () => import('./pages/add/search/search.component'),
          },
          {
            path: ':productId',
            loadComponent: () => import('./pages/add/form/form.component'),
          },
          {
            path: '**',
            redirectTo: '/mde-inventur/add/search',
          },
        ],
      },
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./pages/main/main.component'),
        resolve: {
          activeInventory: inventoryResolveFn,
        },
      },
    ],
  },
];
